import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export type FeedbackType = 'bug_report' | 'feature_request' | 'general';
export type FeedbackStatus = 'new' | 'read' | 'in_progress' | 'live' | 'done';

export interface SubmitFeedbackRequest {
  feedbackType: FeedbackType;
  // Bug Report fields
  overview?: string;
  currentBehavior?: string;
  desiredBehavior?: string;
  // Feature Request fields
  featureOverview?: string;
  coreProblem?: string;
  proposedSolution?: string;
  // General Feedback fields
  sentiment?: 'frustrated' | 'neutral' | 'happy';
  generalFeedback?: string;
  responsePreference?: 'yes' | 'no' | 'no_preference';
  // Image attachments
  images?: File[];
}

export interface SubmitFeedbackResponse {
  success: boolean;
  message?: string;
  feedbackId?: string;
}

export interface FeedbackItem {
  id: string;
  userId: number;
  user?: {
    id: number;
    username: string;
    email: string;
    avatar?: string;
  };
  feedbackType: FeedbackType;
  overview?: string;
  currentBehavior?: string;
  desiredBehavior?: string;
  featureOverview?: string;
  coreProblem?: string;
  proposedSolution?: string;
  sentiment?: 'frustrated' | 'neutral' | 'happy';
  generalFeedback?: string;
  responsePreference?: 'yes' | 'no' | 'no_preference';
  createdAt: string;
  status?: FeedbackStatus;
  images?: { id: string; url: string }[];
}

export interface GetFeedbackResponse {
  feedback: FeedbackItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface GetFeedbackRequest {
  page?: number;
  pageSize?: number;
  feedbackType?: FeedbackType;
  status?: string;
  search?: string;
}

export const feedbackApi = createApi({
  reducerPath: 'feedbackApi',
  baseQuery: baseQuery,
  tagTypes: ['Feedback', 'AdminFeedback'],
  endpoints: (builder) => ({
    submitFeedback: builder.mutation<SubmitFeedbackResponse, SubmitFeedbackRequest>({
      query: ({ images, ...textFields }) => {
        if (images && images.length > 0) {
          const formData = new FormData();
          formData.append('data', JSON.stringify(textFields));
          images.forEach(file => formData.append('images', file));
          return {
            url: '/feedback',
            method: 'POST',
            body: formData,
            requiresAuth: true,
          };
        }
        return {
          url: '/feedback',
          method: 'POST',
          body: textFields,
          requiresAuth: true,
        };
      },
      invalidatesTags: ['AdminFeedback'],
    }),
    
    getUserFeedback: builder.query<GetFeedbackResponse, GetFeedbackRequest>({
      query: ({ page = 1, pageSize = 50 }) => ({
        url: `/feedback?page=${page}&pageSize=${pageSize}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Feedback'],
    }),
    
    getAdminFeedback: builder.query<GetFeedbackResponse, GetFeedbackRequest>({
      query: ({ page = 1, pageSize = 50, feedbackType, status, search }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: pageSize.toString(),
        });
        
        if (feedbackType) params.append('feedbackType', feedbackType);
        if (status) params.append('status', status);
        if (search) params.append('search', search);
        
        return {
          url: `/admin/feedback?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['AdminFeedback'],
    }),
    
    getAdminFeedbackStats: builder.query<any, void>({
      query: () => ({
        url: '/admin/feedback/stats',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminFeedback'],
    }),
    
    updateFeedbackStatus: builder.mutation<any, { id: string; status: FeedbackStatus }>({
      query: ({ id, status }) => ({
        url: `/admin/feedback/${id}/status`,
        method: 'PUT',
        body: { status },
        requiresAuth: true,
      }),
      invalidatesTags: ['AdminFeedback'],
    }),
  }),
});

export const { 
  useSubmitFeedbackMutation,
  useGetUserFeedbackQuery,
  useGetAdminFeedbackQuery,
  useGetAdminFeedbackStatsQuery,
  useUpdateFeedbackStatusMutation
} = feedbackApi;